import React, { useMemo, useState } from 'react';
import { Switch, Radio, Space, Typography, Divider, Alert, Button, Table, InputNumber, Select, Tag } from 'antd';
import { DEFAULT_SIMPLE_RULES, FirewallAction, FirewallDirection } from '../Device/Simulator/Firewall';
import { ProtocolType } from '../Device/Simulator/Ethernet';
import { ServerPageDecoration } from './ServerPageDecoration';

const { Title, Text } = Typography;
const { Option } = Select;

// 协议选项（专业模式按需求仅保留 ICMP / TCP / UDP）
const protocolOptions = [
  { label: 'ICMP', value: ProtocolType.ICMP },
  { label: 'TCP', value: ProtocolType.TCP },
  { label: 'UDP', value: ProtocolType.UDP },
];

// 方向选项
const directionOptions = [
  { label: '入站', value: FirewallDirection.Inbound },
  { label: '出站', value: FirewallDirection.Outbound },
];

// 动作选项
const actionOptions = [
  { label: '允许', value: FirewallAction.Allow },
  { label: '拒绝', value: FirewallAction.Deny },
];

// 自定义规则表格
function RulesTable({ rules, onChange, canEdit }) {
  const columns = [
    {
      title: '方向',
      dataIndex: 'direction',
      render: (value, _, index) => (
        <Select
          disabled={!canEdit}
          defaultValue={value}
          onChange={(v) => {
            const next = rules.map((r, idx) => (idx === index ? { ...r, direction: v } : r));
            onChange(next);
          }}
        >
          {directionOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: '协议',
      dataIndex: 'protocol',
      render: (value, _, index) => (
        <Select
          disabled={!canEdit}
          defaultValue={value}
          onChange={(v) => {
            const next = rules.map((r, idx) => (idx === index ? { ...r, protocol: v } : r));
            onChange(next);
          }}
        >
          {protocolOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: '端口（仅 TCP/UDP）',
      dataIndex: 'port',
      render: (value, record, index) => {
        const isPortProtocol = record.protocol === ProtocolType.TCP || record.protocol === ProtocolType.UDP;
        if (!isPortProtocol) {
          return <span style={{ color: '#999' }}>—</span>;
        }
        return (
          <InputNumber
            disabled={!canEdit}
            min={1}
            max={65535}
            defaultValue={value}
            onChange={(v) => {
              const next = rules.map((r, idx) => (idx === index ? { ...r, port: v || undefined } : r));
              onChange(next);
            }}
          />
        );
      },
    },
    {
      title: '动作',
      dataIndex: 'action',
      render: (value, _, index) => (
        <Select
          disabled={!canEdit}
          defaultValue={value}
          onChange={(v) => {
            const next = rules.map((r, idx) => (idx === index ? { ...r, action: v } : r));
            onChange(next);
          }}
        >
          {actionOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
          ))}
        </Select>
      ),
    },
  ];

  if (canEdit) {
    columns.push({
      title: '操作',
      dataIndex: 'action',
      render: (_, __, index) => (
        <Button
          danger
          size="small"
          onClick={() => {
            const next = rules.filter((_, idx) => idx !== index);
            onChange(next);
          }}
        >
          删除
        </Button>
      ),
    });
  }

  const dataSource = rules.map((rule, idx) => ({ key: rule.id || idx, ...rule }));

  return (
    <Table
      size="small"
      pagination={false}
      columns={columns}
      dataSource={dataSource}
    />
  );
}

export function Firewall(props) {
  const { device } = props;
  const canEdit = true;

  const initialState = useMemo(() => {
    if (device && device.getConfig) {
      return device.getConfig();
    }
    return {
      enabled: true,
      simpleMode: true,
      rules: DEFAULT_SIMPLE_RULES,
    };
  }, [device]);

  const [enabled, setEnabled] = useState(initialState.enabled);
  const [simpleMode, setSimpleMode] = useState(initialState.simpleMode);
  const [rules, setRules] = useState(initialState.rules || DEFAULT_SIMPLE_RULES);

  const applyState = (next = {}) => {
    const nextEnabled = next.enabled !== undefined ? next.enabled : enabled;
    const nextSimpleMode = next.simpleMode !== undefined ? next.simpleMode : simpleMode;
    const nextRules = next.rules || rules;

    if (device) {
      if (device.setEnabled) {
        device.setEnabled(nextEnabled);
      }
      if (device.setSimpleMode) {
        device.setSimpleMode(nextSimpleMode);
      }
      if (!nextSimpleMode && device.updateRules) {
        device.updateRules(nextRules);
      }
    }
  };

  const handleToggle = (checked) => {
    if (!canEdit) return;
    setEnabled(checked);
    applyState({ enabled: checked });
  };

  const handleModeChange = (e) => {
    if (!canEdit) return;
    const mode = e.target.value === 'simple';
    setSimpleMode(mode);
    applyState({ simpleMode: mode });
  };

  const handleRulesChange = (nextRules) => {
    if (!canEdit) return;
    const withId = nextRules.map((r, idx) => ({
      id: r.id || `rule-${idx}-${Date.now()}`,
      ...r,
    }));
    setRules(withId);
    applyState({ rules: withId, simpleMode: false });
  };

  const handleAddRule = () => {
    if (!canEdit) return;
    const next = [
      ...rules,
      {
        id: `rule-${rules.length}-${Date.now()}`,
        direction: FirewallDirection.Outbound,
        protocol: ProtocolType.HTTP,
        action: FirewallAction.Allow,
      },
    ];
    handleRulesChange(next);
  };

  return (
    <ServerPageDecoration fullWidth paddingTop={12} maxWidth={760}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: '#f7f9fb', border: '1px solid #eef1f5' }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>防火墙</Title>
            <Switch
              checkedChildren="开启"
              unCheckedChildren="关闭"
              checked={enabled}
              onChange={handleToggle}
              disabled={!canEdit}
            />
          </Space>
          <Tag color={enabled ? 'green' : 'red'}>{enabled ? '已启用' : '已关闭'}</Tag>
        </div>

        {!enabled ? (
          <Alert type="info" showIcon message="防火墙关闭，内外网完全互通（默认配置）。" />
        ) : (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Text strong>模式</Text>
                <Radio.Group
                  value={simpleMode ? 'simple' : 'pro'}
                  onChange={handleModeChange}
                  disabled={!canEdit}
                  buttonStyle="solid"
                >
                  <Radio.Button value="simple">简单模式</Radio.Button>
                  <Radio.Button value="pro">专业模式</Radio.Button>
                </Radio.Group>
              </div>

              {simpleMode ? (
                <Alert
                  type="success"
                  showIcon
                  message="简单模式规则：ICMP 内出允许、外入拒绝；HTTP/HTTPS 开放；内部可访问外部，外部不可访问内部。"
                />
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text strong>自定义规则</Text>
                    <Button type="primary" onClick={handleAddRule} disabled={!canEdit}>新增规则</Button>
                  </div>
                  <div style={{ background: '#f9fafc', border: '1px solid #eef1f5', borderRadius: 8, padding: 12 }}>
                    <RulesTable rules={rules} onChange={handleRulesChange} canEdit={canEdit} />
                  </div>
                </>
              )}
            </Space>
          </>
        )}
      </Space>
    </ServerPageDecoration>
  );
}

export default Firewall;